import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudioBus,
  BOARD_IDLE,
  ROAD_SOUNDS,
  reduceBoard,
  spinModeFor,
  WHEEL_RUN_CARD,
  type BoardEvent,
  type BoardRunView,
  type MiniGameEnd,
  type PlayerProfile,
  type SlotDemoScene,
} from '@goodtime/engine';
import {
  hiloFriend,
  isWheelRun,
  lampGameTitle,
  slotGameTitle,
  type BoardRunState,
  type HiLoOffer,
  type PlayOffer,
  type RoadRestoreResponse,
  type RoadState,
  type SpinSlotResponse,
} from '@goodtime/shared';
import { funnelBeacon } from '../api/funnel';
import { restoreLandmark } from '../api/restore';
import { noteAccountMoment } from '../lib/accountEvents';
import { takeRestoredWallet } from '../components/journey/restoreWallet';
import { collectBoard, fetchBoard, fetchRoad, runFromConflict, stopBoard, withRetry } from '../api/road';
import { playGame } from '../components/minigames/registry';
import { hiloApi, offerOfPlay, startFirstPlay, type FirstPlayLamp } from '../api/roadFriends';
import { useFriendArrivals } from './useFriendArrivals';
import { useRoadGift } from './useRoadGift';

/** How long a landing shows before the light goes round again. */
const RESULT_MS = 900;

export interface SlotJourneyOptions {
  sessionId: string | null;
  /** For the funnel beacons. */
  playerId?: string | null;
  themeId: string | null;
  enabled: boolean;
  /** The slot scene, read when needed (it changes as slots open). */
  getScene: () => SlotDemoScene | null;
  /** True while a dialog is open: Space must not STOP behind it. */
  isBlocked: () => boolean;
  /** Pull the server wallet after a collect or a Hi-Lo payout. */
  syncWallet: () => Promise<void>;
  /** The player's profile: a restore's answer sets its balance, the
   *  gift square's gems show on it, and it remembers the Wheel Run card. */
  profile: Pick<PlayerProfile, 'reconcileBalance' | 'awardGems' | 'wheelRunCardSeen' | 'markWheelRunCardSeen'>;
  /** The slot opened with no road from the server: it's still the Sweet
   *  Trail's while a bonus there waits to be played (the server converts
   *  the row once it has been). */
  onNoRoad?: (themeId: string) => void;
}

/**
 * A flagged player's Royal Road on the open slot: the road (loaded when the
 * slot opens, updated by each spin), and the Lamp Run, played against the
 * server. A run starts once the spin that earned it has finished; STOP and
 * collect retry with the same key when the connection drops, so a retried
 * request pays once; a run left open is resumed when the slot opens again.
 * The same road runs as the Wheel Run on the slots without one of their own
 * (`wheel_run`): lamps lit by the slot's stars, with games named after the
 * slot. On every road the gift square's gift opens when it's reached.
 */
export function useSlotJourney({
  sessionId,
  playerId,
  themeId,
  enabled,
  getScene,
  isBlocked,
  syncWallet,
  profile,
  onNoRoad,
}: SlotJourneyOptions) {
  const [road, setRoad] = useState<RoadState | null>(null);
  const roadRef = useRef<RoadState | null>(null);
  roadRef.current = road;
  const [spinKey, setSpinKey] = useState(0);
  const [view, setView] = useState<BoardRunView>(BOARD_IDLE);
  const [notice, setNotice] = useState<string | null>(null);
  const [bonus, setBonus] = useState<HiLoOffer | null>(null);
  const viewRef = useRef<BoardRunView>(BOARD_IDLE);
  const pendingRun = useRef<BoardRunState | null>(null);
  // A mini-game is on screen: STOP and COLLECT wait for it, and the slot's
  // other controls step aside (runActive) until it closes.
  const gameOpen = useRef(false);
  const [gameShown, setGameShown] = useState(false);
  /** False from a spin's answer until its reels and wins have finished. */
  const [slotIdle, setSlotIdle] = useState(true);
  /** Bumped when the slot's scene is up: a card waiting for it can show. */
  const [sceneKey, setSceneKey] = useState(0);
  /** Plays a lit lamp's game (set below, once openGame exists). */
  const lampGame = useRef<(offer: PlayOffer) => void>(() => undefined);
  const apiRef = useRef({ sessionId, playerId, themeId, enabled, getScene, isBlocked, syncWallet, profile, onNoRoad });
  apiRef.current = { sessionId, playerId, themeId, enabled, getScene, isBlocked, syncWallet, profile, onNoRoad };

  const dispatch = useCallback((event: BoardEvent) => {
    const { view: next, effect } = reduceBoard(viewRef.current, event);
    viewRef.current = next;
    setView(next);
    apiRef.current.getScene()?.setSpinMode(spinModeFor(next));
    return effect;
  }, []);

  /** A lit lamp's game was open when the run was left (a reload, another
   *  tab): open it again. */
  const reopenLampGame = useCallback((run: BoardRunState) => {
    const sid = apiRef.current.sessionId;
    if (!run.child || !sid) return;
    void offerOfPlay(run.child.playId, sid)
      .then((offer) => lampGame.current(offer))
      .catch(() => setNotice('Lost the connection. Open the slot again to carry on.'));
  }, []);

  const beginRun = useCallback(
    (run: BoardRunState) => {
      const scene = apiRef.current.getScene();
      if (!scene) {
        pendingRun.current = run;
        return;
      }
      setNotice(null);
      scene.startBoardRun(run);
      dispatch({ type: 'start', run });
      reopenLampGame(run);
      funnelBeacon('run_seen', { playerId: apiRef.current.playerId, detail: `${run.origin}${run.lucky ? ':lucky' : ''}` });
    },
    [dispatch, reopenLampGame],
  );

  // Load the road, and resume a run left open, when the slot opens.
  useEffect(() => {
    setRoad(null);
    viewRef.current = BOARD_IDLE;
    setView(BOARD_IDLE);
    pendingRun.current = null;
    if (!enabled || !sessionId || !themeId) return;
    let cancelled = false;
    void fetchRoad(sessionId, themeId).then(async (r) => {
      if (cancelled) return;
      setRoad(r);
      if (!r) {
        apiRef.current.onNoRoad?.(themeId);
        return;
      }
      const run = await fetchBoard(sessionId, themeId);
      if (!cancelled && run && run.status === 'open') {
        const scene = apiRef.current.getScene();
        if (scene?.idle) beginRun(run);
        else pendingRun.current = run;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, sessionId, themeId, beginRun]);

  /** Take the road from a spin response, and hold any run it started until
   *  the spin has finished. Returns the gems its squares paid. */
  const onSpinLanded = useCallback((response: SpinSlotResponse): number => {
    if (!response.road) return 0;
    setSlotIdle(false);
    setRoad(response.road);
    setSpinKey((k) => k + 1);
    if (response.boardRun) pendingRun.current = response.boardRun;
    return response.road.trail.steps.reduce((n, s) => n + s.gems, 0);
  }, []);

  /** The slot is idle: start a run that's waiting. */
  const onSlotIdle = useCallback(() => {
    setSlotIdle(true);
    const run = pendingRun.current;
    if (!run || viewRef.current.phase !== 'idle') return;
    pendingRun.current = null;
    beginRun(run);
  }, [beginRun]);

  /** The slot's scene is up: start a run found open before it was (a
   *  reload where the server answered before the scene was built, as a
   *  slower phone does). Otherwise that run waited for the next spin. */
  const onSceneReady = useCallback(() => {
    setSceneKey((k) => k + 1);
    const run = pendingRun.current;
    if (!run || viewRef.current.phase !== 'idle' || !apiRef.current.getScene()?.idle) return;
    pendingRun.current = null;
    beginRun(run);
  }, [beginRun]);

  /** A spin was refused because a run is open: show that run. */
  const resumeRun = useCallback(
    (run: BoardRunState) => {
      if (viewRef.current.phase === 'idle') beginRun(run);
    },
    [beginRun],
  );

  const onStopPressed = useCallback(async () => {
    const { sessionId: sid, getScene: scene, isBlocked: blocked } = apiRef.current;
    if (blocked() || !sid || gameOpen.current) return;
    const effect = dispatch({ type: 'stopPressed' });
    if (effect?.type !== 'requestStop' || !viewRef.current.runId) return;
    const runId = viewRef.current.runId;
    try {
      const result = await withRetry(() => stopBoard(sid, runId, effect.stop));
      await scene()?.boardLandOn(result);
      dispatch({ type: 'landed', result });
      setNotice(null);
      if (result.child) {
        // A lit lamp: its friend's game opens, and the run waits for it.
        lampGame.current(result.child);
        return;
      }
      window.setTimeout(() => {
        dispatch({ type: 'resultShown' });
        if (viewRef.current.phase === 'ready') scene()?.boardResume();
      }, RESULT_MS);
    } catch (err) {
      const run = runFromConflict(err);
      if (run) {
        // Out of step with the server (another tab, a lost answer): catch up.
        scene()?.startBoardRun(run);
        dispatch({ type: 'start', run });
        reopenLampGame(run);
        return;
      }
      dispatch({ type: 'stopFailed' });
      setNotice('Lost the connection. Tap STOP to try again.');
    }
  }, [dispatch, reopenLampGame]);

  const onCollectPressed = useCallback(async () => {
    const { sessionId: sid, getScene: scene, isBlocked: blocked, syncWallet: sync } = apiRef.current;
    if (blocked() || !sid || gameOpen.current) return;
    const effect = dispatch({ type: 'collectPressed' });
    if (effect?.type !== 'requestCollect' || !viewRef.current.runId) return;
    const runId = viewRef.current.runId;
    try {
      const res = await withRetry(() => collectBoard(sid, runId));
      setRoad(res.road);
      scene()?.addFreeSpins(res.credited.freeSpins);
      const paid = res.credited.coins > 0 || res.credited.freeSpins > 0;
      AudioBus.shared.play(paid ? ROAD_SOUNDS.collected : ROAD_SOUNDS.hiloEnd, { volume: 0.8 });
      if (paid) AudioBus.shared.play(ROAD_SOUNDS.coinBurst, { volume: 0.7 });
      // The pot's coins fly into the coin counter (crediting as they land),
      // then the wallet catches up with the server's.
      const s = scene();
      if (s && res.credited.coins > 0) await s.flyCoinsToWallet(s.runCoinSource, res.credited.coins);
      await sync();
      dispatch({ type: 'collected' });
      scene()?.endBoardRun();
      dispatch({ type: 'end' });
      setNotice(null);
      if (res.bonus) setBonus(res.bonus);
    } catch (err) {
      const run = runFromConflict(err) ?? (themeId ? await fetchBoard(sid, themeId) : null);
      if (run && run.status === 'open') {
        scene()?.startBoardRun(run);
        dispatch({ type: 'start', run });
        setNotice('Couldn’t collect just now. Tap COLLECT to try again.');
      } else {
        // Already collected (the answer was lost): just tidy up.
        await sync();
        scene()?.endBoardRun();
        dispatch({ type: 'end' });
      }
    }
  }, [dispatch, themeId]);

  /**
   * Play a game the server has offered (a lit lamp's, a friend's, a
   * visitor's) on the slot canvas, to its end. A prize paid to the wallet
   * has flown into the coin counter by then; free spins join the slot's,
   * and the wallet catches up with the server's. A lamp's game paid the
   * run's pot instead (`end.pot`). A game that won the gold pot ends with
   * its moment (a title card, the coins flying), and the panel's gold pot
   * starts again. A friend's game is under `title`, its own name.
   */
  const openGame = useCallback(async (offer: PlayOffer, title?: string): Promise<MiniGameEnd> => {
    const { sessionId: sid, getScene: scene, syncWallet: sync } = apiRef.current;
    const s = scene();
    if (!sid || !s || gameOpen.current) return { prize: null };
    gameOpen.current = true;
    setGameShown(true);
    try {
      const end = await playGame(s, offer, sid, title);
      if (end.prize && offer.creditTo !== 'parent') {
        if (end.prize.freeSpins > 0) s.addFreeSpins(end.prize.freeSpins);
        await sync();
      }
      if (end.prize?.gold) {
        await s.goldPotWon(end.prize.coins, offer.creditTo === 'parent');
        const r = themeId ? await fetchRoad(sid, themeId) : null;
        if (r) setRoad(r);
      }
      return end;
    } finally {
      gameOpen.current = false;
      setGameShown(false);
    }
  }, [themeId]);

  /**
   * Play a friend's game, under its name on this road: the Hi-Lo dialog,
   * under the name of the road's own Hi-Lo friend, for Hi-Lo; the mini-game
   * layer for the rest ("SHELL SHUFFLE", not "FIND THE COINS"). A lamp's game
   * pays the run's pot; a free first game pays the wallet (its coins fly
   * there in the game).
   */
  const playFriendGame = useCallback(
    async (offer: PlayOffer): Promise<void> => {
      const { sessionId: sid, getScene: scene, syncWallet: sync } = apiRef.current;
      const s = scene();
      if (!sid || !s) return;
      if (offer.kind !== 'hilo') {
        await openGame(offer, lampGameTitle(themeId, roadRef.current, offer.kind)?.toUpperCase());
        return;
      }
      // A road's Hi-Lo friend's name; on a Wheel Run slot, the slot's own.
      const title = (themeId ? slotGameTitle(themeId, 'hilo') : null) ?? hiloFriend(roadRef.current).title;
      const inRun = offer.creditTo === 'parent';
      gameOpen.current = true;
      setGameShown(true);
      try {
        await s.playHiLo(offer as HiLoOffer, hiloApi(offer.playId, sid), {
          title: title.toUpperCase(),
          sub: inRun ? 'What you win goes in your run pot.' : 'Your first game is free. Ties go your way.',
          intoPot: inRun,
        });
        if (!inRun) await sync();
      } finally {
        gameOpen.current = false;
        setGameShown(false);
      }
    },
    [openGame, themeId],
  );

  /** A lit lamp's game, opened by a landing (or found open after a
   *  reload): play it, show what it added to the run pot, then the run
   *  goes on. */
  lampGame.current = (offer: PlayOffer) => {
    void (async () => {
      const { sessionId: sid, getScene: scene } = apiRef.current;
      await playFriendGame(offer);
      // The run as the server has it now: the pot the game paid into.
      const run = sid && themeId ? await fetchBoard(sid, themeId) : null;
      if (run) {
        dispatch({ type: 'potChanged', pot: run.pot });
        scene()?.boardShowPot(run.pot);
      }
      window.setTimeout(() => {
        dispatch({ type: 'resultShown' });
        if (viewRef.current.phase === 'ready') scene()?.boardResume();
      }, RESULT_MS);
    })();
  };

  /** A lamp's free first game, from its hello (a friend's, or a new game's
   *  card) or its lamp. */
  const startFreePlay = useCallback(
    async (which: FirstPlayLamp) => {
      const sid = apiRef.current.sessionId;
      if (!sid || !themeId || gameOpen.current) return;
      try {
        await playFriendGame(await withRetry(() => startFirstPlay(sid, themeId, which)));
      } catch {
        // Used already (another tab) or offline: the road below says so.
      }
      const r = await fetchRoad(sid, themeId);
      if (r) setRoad(r);
    },
    [playFriendGame, themeId],
  );

  /** Fetch the road again after a change no spin brings: Gamble switched
   *  in Settings, which can change the game a friend's lamp offers, or the
   *  gift square's gift played. */
  const refreshRoad = useCallback(async () => {
    const { sessionId: sid, themeId: theme, enabled: on } = apiRef.current;
    if (!on || !sid || !theme) return;
    const r = await fetchRoad(sid, theme).catch(() => null);
    // Still the same slot's road.
    if (r && apiRef.current.sessionId === sid && apiRef.current.themeId === theme) setRoad(r);
  }, []);

  const busy = !slotIdle || view.phase !== 'idle' || gameShown || bonus !== null;
  const gift = useRoadGift({
    sessionId: enabled ? sessionId : null,
    playerId: playerId ?? null,
    themeId: enabled ? themeId : null,
    road,
    spinKey,
    getScene,
    profile,
    paused: busy,
    onClosed: () => void refreshRoad(),
  });

  useFriendArrivals({
    road,
    themeId,
    getScene,
    paused: busy || gift.playing,
    onFreePlay: (friend) => void startFreePlay({ friend }),
    onLampFreePlay: (lamp) => void startFreePlay({ lamp }),
  });

  // The one-time card on a player's first Wheel Run on a slot that had the
  // Sweet Trail (decision 12): a row whose trail carried over (past the
  // first lap, with no run yet) says what changed, once.
  // It's seen once the player closes it; a scene built again meanwhile (the
  // slot opening as a road) shows it again.
  const cardedOn = useRef<SlotDemoScene | null>(null);
  useEffect(() => {
    const scene = getScene();
    if (busy || gift.playing || !scene || !road || profile.wheelRunCardSeen || cardedOn.current === scene) return;
    // This slot's own scene: the last slot's can still be up as it opens.
    if (scene.themeId !== themeId || !isWheelRun(road) || road.runs > 0 || road.trail.level < 1) return;
    cardedOn.current = scene;
    void scene.showNotice(WHEEL_RUN_CARD.title, WHEEL_RUN_CARD.line).then(() => profile.markWheelRunCardSeen());
  }, [road, busy, gift.playing, getScene, profile, sceneKey, themeId]);

  // Dev builds (and the browser tests) can open any offered game, as a lit
  // lamp or a visitor will.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __offerPlay?: (offer: PlayOffer) => Promise<MiniGameEnd> };
    w.__offerPlay = openGame;
    return () => {
      delete w.__offerPlay;
    };
  }, [openGame]);

  /** Restore landmark `landmark` on this slot's road with coins: the road
   *  and the wallet come back from the server. The balance is taken from the
   *  answer, not a wallet sync, which would count the spend as a bet (XP, and
   *  a level-up over the restore moment). */
  const restore = useCallback(
    async (landmark: number): Promise<RoadRestoreResponse> => {
      const { sessionId: sid, profile: p } = apiRef.current;
      if (!sid || !themeId) throw new Error('No road to restore');
      const res = await restoreLandmark(sid, themeId, landmark);
      setRoad(res.road);
      takeRestoredWallet(p, res);
      // A chapter done is a good moment to offer the account.
      if (res.chapterComplete) noteAccountMoment('chapter');
      return res;
    },
    [themeId],
  );

  return {
    road,
    spinKey,
    refreshRoad,
    restore,
    view,
    notice,
    bonus,
    clearBonus: () => setBonus(null),
    openGame,
    runActive: view.phase !== 'idle' || gameShown || gift.playing,
    onSpinLanded,
    onSlotIdle,
    onSceneReady,
    resumeRun,
    onStopPressed,
    onCollectPressed,
  };
}
